import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SpecialAttribute } from "@/core/entities/special-attribute";
import { useSpecialAttributes } from "@/hooks/repository-hooks/special-attribute/use-special-attribute";
import { ChevronDown } from "lucide-react";
import { useMemo } from "react";
import { SpecialAttributeChip } from "./SpecialAttributeChip";

interface SpecialAttributeMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function SpecialAttributeMultiSelect({
  value,
  onChange,
  disabled,
}: Readonly<SpecialAttributeMultiSelectProps>) {
  const poolQuery = useSpecialAttributes();
  const pool: SpecialAttribute[] = useMemo(
    () => poolQuery.data ?? [],
    [poolQuery.data],
  );

  const poolById = useMemo(() => {
    const map = new Map<string, SpecialAttribute>();
    for (const entry of pool) map.set(entry.id, entry);
    return map;
  }, [pool]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between"
          >
            <span className="text-sm text-muted-foreground">
              {value.length > 0 ? `${value.length} selected` : "Select attributes"}
            </span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-fit min-w-[12rem] p-1" align="start">
          {pool.length === 0 ? (
            <div className="p-2 text-xs text-muted-foreground">
              No special attributes yet.
            </div>
          ) : (
            <div className="max-h-64 overflow-auto">
              {pool.map((entry) => {
                const selected = selectedSet.has(entry.id);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => toggle(entry.id)}
                    className={
                      "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted " +
                      (selected ? "bg-muted/60" : "")
                    }
                  >
                    <SpecialAttributeChip
                      name={entry.name}
                      color={entry.color}
                      className="px-2 py-0.5 text-xs"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const entry = poolById.get(id);
            return (
              <SpecialAttributeChip
                key={id}
                name={entry?.name ?? id}
                color={entry?.color}
                onRemove={() => toggle(id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
